/**
 * LeadSummary — the multi-stat strip under the page-head H1.
 *
 * Customers: `12 customers · ₹X outstanding · 3 with overdue · ₹Y YTD`.
 * Vendors:   `9 vendors · ₹X outstanding · 6 awaiting · ₹Y spent YTD`.
 *
 * Each segment has a label + bold value, separated by 3px round dots.
 * Some segments carry an `accent` tone (e.g. rose for overdue counts > 0).
 *
 * @see prototype/sales/customers.html `.page-head .lead`
 * @see prototype/purchases/vendors.html `.page-head .lead`
 */
import { cn } from "@flint/ui";

export interface LeadSummaryItem {
  /** The value to bold (string or formatted JSX). */
  value: React.ReactNode;
  /** Trailing label after the value (e.g. "outstanding"). */
  label: string;
  /** Optional tone for the entire segment text. Defaults to muted. */
  tone?: "muted" | "warn" | "bad";
  /** Optional title for tooltip / hover-explain (e.g. "wires up with X"). */
  title?: string;
}

export interface LeadSummaryProps {
  items: LeadSummaryItem[];
  loading?: boolean;
}

const TONE_CLASS: Record<NonNullable<LeadSummaryItem["tone"]>, string> = {
  muted: "text-muted",
  warn: "text-warn font-semibold",
  bad: "text-bad font-semibold",
};

export function LeadSummary({ items, loading }: LeadSummaryProps) {
  if (loading) return <p className="text-sm text-muted mt-1.5">Loading…</p>;

  return (
    <p className="text-sm text-muted mt-1.5 flex items-center gap-x-2.5 gap-y-1 flex-wrap">
      {items.map((item, i) => (
        <span key={i} className="contents">
          <span className={cn(TONE_CLASS[item.tone ?? "muted"])} title={item.title}>
            <strong className={cn("num", item.tone === "muted" ? "text-ink" : "")}>
              {item.value}
            </strong>{" "}
            {item.label}
          </span>
          {i < items.length - 1 ? (
            <span aria-hidden className="h-[3px] w-[3px] rounded-full bg-line-2 shrink-0" />
          ) : null}
        </span>
      ))}
    </p>
  );
}
